import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Link } from "react-router-dom";
import { TrendingUp, Package, MousePointerClick, Star, ArrowUpRight } from "lucide-react";
import { LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid, BarChart, Bar } from "recharts";
import { platformLabel } from "../lib/platforms";

export default function AdminDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/analytics/overview").then((r) => setData(r.data)).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="p-12">Loading…</div>;

  const cards = [
    { label: "Total products", value: data.total_products, Icon: Package },
    { label: "Total clicks", value: data.total_clicks, Icon: MousePointerClick },
    { label: "Active platforms", value: data.platforms.length, Icon: Star },
    { label: "Top product clicks", value: data.top_products[0]?.clicks || 0, Icon: TrendingUp },
  ];

  return (
    <div className="p-8 lg:p-12" data-testid="admin-dashboard">
      <div className="flex items-end justify-between mb-10">
        <div>
          <p className="overline text-white/40">Operator</p>
          <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter mt-2">Control room.</h1>
        </div>
        <Link to="/admin/products" data-testid="add-product-cta" className="bg-[#E53935] text-white px-5 py-3 text-sm uppercase tracking-wider font-semibold hover:bg-[#C62828] transition-colors">
          + Add product
        </Link>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
        {cards.map((c, i) => (
          <div key={i} className="admin-card p-6" data-testid={`stat-card-${i}`}>
            <div className="flex items-center justify-between">
              <p className="overline text-white/40">{c.label}</p>
              <c.Icon className="w-4 h-4 text-[#E53935]" strokeWidth={1.5} />
            </div>
            <p className="font-display font-black text-5xl tracking-tighter mt-4">{c.value}</p>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mt-8">
        <div className="admin-card p-6 lg:col-span-2">
          <p className="overline text-white/40">Clicks · last 14 days</p>
          <div className="h-64 mt-4">
            <ResponsiveContainer>
              <LineChart data={data.daily_clicks}>
                <CartesianGrid stroke="#1f1f1f" strokeDasharray="3 3" />
                <XAxis dataKey="date" stroke="#666" fontSize={11} />
                <YAxis stroke="#666" fontSize={11} />
                <Tooltip contentStyle={{ background: "#0A0A0A", border: "1px solid #2a2a2a", color: "#fff" }} />
                <Line type="monotone" dataKey="clicks" stroke="#E53935" strokeWidth={2} dot={{ fill: "#E53935", r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="admin-card p-6">
          <p className="overline text-white/40">By platform</p>
          <div className="h-64 mt-4">
            <ResponsiveContainer>
              <BarChart data={data.platforms.map(p => ({ name: platformLabel(p.platform), clicks: p.clicks }))}>
                <CartesianGrid stroke="#1f1f1f" strokeDasharray="3 3" />
                <XAxis dataKey="name" stroke="#666" fontSize={10} />
                <YAxis stroke="#666" fontSize={11} />
                <Tooltip contentStyle={{ background: "#0A0A0A", border: "1px solid #2a2a2a", color: "#fff" }} />
                <Bar dataKey="clicks" fill="#E53935" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="admin-card mt-8">
        <div className="p-6 border-b border-white/10 flex justify-between items-center">
          <p className="overline text-white/40">Top products</p>
          <Link to="/admin/analytics" className="text-xs uppercase tracking-wider text-white/60 hover:text-white inline-flex items-center gap-1">
            Full analytics <ArrowUpRight className="w-3 h-3" />
          </Link>
        </div>
        <div className="divide-y divide-white/5">
          {data.top_products.length === 0 ? (
            <div className="p-8 text-center text-white/50 text-sm">No clicks yet. Add a product and start sharing your affiliate links.</div>
          ) : data.top_products.map((p) => (
            <div key={p.id} className="p-4 flex items-center gap-4">
              <img src={p.image_url} alt="" className="w-12 h-12 object-cover" />
              <div className="flex-1">
                <p className="font-semibold text-sm">{p.title}</p>
                <p className="text-xs text-white/40 uppercase tracking-wider">{platformLabel(p.platform)}</p>
              </div>
              <p className="font-display font-black text-2xl tracking-tighter text-[#E53935]">{p.clicks}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
